import { CoachConversationFlow, GoalScope, type CoachConversation } from '../../../../../../pure-js/types/CoachTypes'
import type { CoachStatus } from '../../../../../../pure-js/types/CoachApi'
import { isLeadershipConversation } from '../../../../../../pure-js/libs/AICoachStateHelper'

// Which of the two conversations a leader is having: their own development, or how they lead.

const STORAGE_KEY_PREFIX = 'intraction_coach_focus_'

export const SUPPORTED_FOCUSES: string[] = Object.values(GoalScope)

/** Unknown counts as goal-setting: that is the reading that shows the cautious copy while the status loads. */
export const isFirstGoalSettingFor = (coachStatus: CoachStatus | undefined): boolean =>
  coachStatus?.onboardingGoalsCompleted !== true

export const isConversationOfFocus = (conversation: CoachConversation, focus: GoalScope): boolean =>
  isLeadershipConversation(conversation) === (focus === GoalScope.LEADERSHIP)

export const focusOfConversation = (conversation?: CoachConversation): GoalScope =>
  isLeadershipConversation(conversation) ? GoalScope.LEADERSHIP : GoalScope.INDIVIDUAL

/** A leadership talk is asked for by name; every other kind is left for the server to infer. */
export const flowOfFocus = (focus: GoalScope): CoachConversationFlow | undefined =>
  focus === GoalScope.LEADERSHIP ? CoachConversationFlow.LEADERSHIP : undefined

/** The focus in use: personal unless leadership is on offer; then the one chosen, else leadership on a goal-setting
 * visit, else the one picked on an earlier visit, else leadership. */
export function resolveCoachFocus(params: {
  isLeadershipOffered: boolean
  /** Goals were still due at some point of this visit; the ones landing mid-talk do not end it. */
  isGoalSettingVisit: boolean
  /** Picked on an earlier visit on this device; none when they never picked one. */
  storedFocus?: GoalScope
  /** Picked on this visit, which no default overrides. */
  chosenFocus?: GoalScope
}): GoalScope {
  const { isLeadershipOffered, isGoalSettingVisit, storedFocus, chosenFocus } = params
  if (!isLeadershipOffered) return GoalScope.INDIVIDUAL
  if (chosenFocus) return chosenFocus
  if (isGoalSettingVisit) return GoalScope.LEADERSHIP

  return storedFocus ?? GoalScope.LEADERSHIP
}

export function readStoredFocus(userId?: string): GoalScope | undefined {
  if (!userId) return undefined
  try {
    const stored = window.localStorage.getItem(`${STORAGE_KEY_PREFIX}${userId}`)
    return SUPPORTED_FOCUSES.includes(stored || '') ? (stored as GoalScope) : undefined
  } catch {
    // Private mode or storage disabled: nothing was remembered, so the default focus applies.
    return undefined
  }
}

export function rememberFocus(userId: string, focus: GoalScope): void {
  try {
    window.localStorage.setItem(`${STORAGE_KEY_PREFIX}${userId}`, focus)
  } catch {
    // Not being able to remember only costs the choice at the next visit.
  }
}
